import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {NgSelectModule} from "@ng-select/ng-select";
import {AdminDashboardFilterDto} from "./admin-dashboard-filter.dto";
import {
    AdminUserStatusEnum,
    ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS
} from "@shared/api/models/query/admin-user.model";
import {OrganisationsService} from "@shared/api/services/v2/organisations.service";
import {ServicesService} from "@shared/api/services/v2/services.service";
import {DepartmentsService} from "@shared/api/services/v2/departments.service";
import {CareProgramsService} from "@shared/api/services/v2/care-programs.service";

@Component({
    selector: 'cdss-admin-dashboard-filter',
    imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
    standalone: true,
    templateUrl: './admin-dashboard-filter.component.html',
    styleUrl: './admin-dashboard-filter.component.scss'
})
export class AdminDashboardFilterComponent implements OnInit {

    form!: FormGroup;

    @Output() apply = new EventEmitter<boolean>();
    @Output() cancel = new EventEmitter<{}>();

    _filter!: AdminDashboardFilterDto;
    @Output() filterChange = new EventEmitter<AdminDashboardFilterDto>();

    get filter(): AdminDashboardFilterDto {
        return this._filter;
    }

    @Input()
    set filter(value: AdminDashboardFilterDto) {
        this._filter = value;
        this.initForm();
    }

    // Dropdown options
    // The full engagement option set (values + labels) is defined next to
    // AdminUserEngagementEnum in admin-user.model.ts so the backend
    // `engagement` contract (UNIVAPLAT-2573/2574) lives in one place.
    engagementOptions = ADMIN_DASHBOARD_ENGAGEMENT_OPTIONS;
    roleOptions = [
        {name: 'Patient', value: 'patient'},
        {name: 'Carer', value: 'carer'},
    ];
    // Values come from AdminUserStatusEnum so the UI options cannot drift
    // from the backend's derived status set (UNIVAPLAT-2357). Draft is
    // intentionally excluded — admins must not be able to filter it back in.
    // 'Not logged in' is the opposite case (UNIVAPLAT-2798): deliberately
    // visible and filterable, unlike Draft.
    statusOptions = [
        {name: 'Not logged in', value: AdminUserStatusEnum.NOT_LOGGED_IN},
        {name: 'First Login Completed', value: AdminUserStatusEnum.FIRST_LOGIN_COMPLETED},
        {name: 'Onboarding Completed', value: AdminUserStatusEnum.ONBOARDING_COMPLETED},
        {name: 'Active', value: AdminUserStatusEnum.ACTIVE},
        {name: 'Completed', value: AdminUserStatusEnum.COMPLETED},
    ];
    licenseOptions = [
        {name: 'Essentials', value: 'essentials'},
        {name: 'Comfort', value: 'comfort'},
        {name: 'Therapy', value: 'therapy'}
    ];
    categoryOptions = [
        {name: 'DTC', value: 'dtc'},
        {name: 'NHS', value: 'nhs'}
    ]
    careProgrammeOptions: any[] = [];
    organisationOptions: any[] = [];
    serviceOptions: any[] = [];
    departmentOptions: any[] = [];
    weekOptions: any[] = this.initializeCurrentWeek();

    constructor(
        private fb: FormBuilder,
        private organisationsService: OrganisationsService,
        private servicesService: ServicesService,
        private departmentsService: DepartmentsService,
        private careProgramsService: CareProgramsService,
    ) {
        this.form = this.fb.group({
            engagement: [[]],
            role: [[]],
            license: [[]],
            category: [[]],
            status: [[]],
            currentWeek: [[]],
            includeDemo: [false],
            careProgramme: [[]],
            organisation: [[]],
            service: [[]],
            department: [[]],
            signupDateStart: [''],
            signupDateEnd: [''],
            firstLoginDateStart: [''],
            firstLoginDateEnd: [''],
            lastLoginDateStart: [''],
            lastLoginDateEnd: ['']
        });
    }

    ngOnInit() {
        this.getOrganisation();
        this.getService();
        this.getDepartment();
        this.getCarePrograms();
    }

    async getOrganisation() {
        const organisation = await this.organisationsService.listAll()
        this.organisationOptions = organisation.data.map((org) => {
            return{
                id: org.id,
                name: org.name
            }
        });
    }

    async getService() {
        const services = await this.servicesService.listAll()
        this.serviceOptions = services.data.map((service) => {
            return{
                id: service.id,
                name: service.name
            }
        });
    }

    async getDepartment() {
        const departments = await this.departmentsService.listAll()
        this.departmentOptions = departments.data.map((department) => {
            return{
                id: department.id,
                name: department.name
            }
        });
    }

    async getCarePrograms() {
        const carePrograms = await this.careProgramsService.listAll()
        this.careProgrammeOptions = carePrograms.data.map((careProgram) => {
            return{
                id: careProgram.id,
                name: careProgram.title
            }
        });
    }

    initializeCurrentWeek() {
        const weekGroup = [];
        for (let i = 1; i <= 20; i++) {
            weekGroup.push({
                name: `Week ${i}`,
                value: `${i}`,
            });
        }
        return weekGroup;
    }

    initForm() {
        if (!this.filter) return;

        this.form.reset({
            engagement: this.filter.engagement || [],
            role: this.filter.role || [],
            license: this.filter.license || [],
            category: this.filter.category || [],
            status: this.filter.status || [],
            currentWeek: this.filter.currentWeek || [],
            includeDemo: this.filter.includeDemo || false,
            careProgramme: this.filter.careProgramme || [],
            organisation: this.filter.organisation || [],
            service: this.filter.service || [],
            department: this.filter.department || [],
            signupDateStart: this.filter.signupDateStart || '',
            signupDateEnd: this.filter.signupDateEnd || '',
            firstLoginDateStart: this.filter.firstLoginDateStart || '',
            firstLoginDateEnd: this.filter.firstLoginDateEnd || '',
            lastLoginDateStart: this.filter.lastLoginDateStart || '',
            lastLoginDateEnd: this.filter.lastLoginDateEnd || ''
        });
    }

    reset() {
        this.form.reset({
            engagement: [],
            role: [],
            license: [],
            category: [],
            status: [],
            currentWeek: [],
            includeDemo: false,
            careProgramme: [],
            organisation: [],
            service: [],
            department: [],
            signupDateStart: '',
            signupDateEnd: '',
            firstLoginDateStart: '',
            firstLoginDateEnd: '',
            lastLoginDateStart: '',
            lastLoginDateEnd: ''
        });
        this.returnValue();
        this.apply.emit();
    }

    applyFilter() {
        this.returnValue();
        this.apply.emit();
    }

    returnValue() {
        const value = this.form.value;
        this.filter.engagement = value.engagement || [];
        this.filter.role = value.role || [];
        this.filter.license = value.license || [];
        this.filter.category = value.category || [];
        this.filter.status = value.status || [];
        this.filter.currentWeek = value.currentWeek || [];
        this.filter.includeDemo = value.includeDemo || false;
        this.filter.careProgramme = value.careProgramme || [];
        this.filter.organisation = value.organisation || [];
        this.filter.service = value.service || [];
        this.filter.department = value.department || [];
        this.filter.signupDateStart = value.signupDateStart || '';
        this.filter.signupDateEnd = value.signupDateEnd || '';
        this.filter.firstLoginDateStart = value.firstLoginDateStart || '';
        this.filter.firstLoginDateEnd = value.firstLoginDateEnd || '';
        this.filter.lastLoginDateStart = value.lastLoginDateStart || '';
        this.filter.lastLoginDateEnd = value.lastLoginDateEnd || '';

        this.filterChange.emit(this.filter);
    }

    close() {
        this.cancel.emit({});
    }
}
